import {
    IDashboardResponse,
    IEdgeDefinitionResponse,
    IEdgeImageRepositoryResponse,
    IIssueActivityChartDataResponse,
    IIssueActivityCommentReplyResponse,
    IIssueActivityCommentResponse,
    IIssueResponseBase,
    ISystemResponse,
    MachineHealthEventType,
} from '@kcftech/prometheustypescriptapiclient';
import { PermissionsContextDef } from 'contexts/context.shared';
import {
    getUserPermissions,
    useOrganizationPermissionsForUser,
} from 'contexts/getUserPermissions';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useUser } from 'contexts/UserContext';
import { IssueMode } from 'pages/Desk/issues/shared';
import { Subtree } from 'pages/workbench/side_panel/workbench_tree/useLoadSubtree';
import { ReactNode, useContext, useMemo } from 'react';
import { TreeItem } from 'utils/tree';

export type TreeItemActionOptions = Readonly<{
    system: ISystemResponse;
    treeItem: Subtree;
}>;

export type PermissionsContext = Readonly<{
    canViewDebug: boolean;
    canEnableFeatureFlags: boolean;
    /**
     * Whether the asset metadata field can be seen,
     * only corporate admin and up.
     */
    canViewAssetMetadata: boolean;
    canViewAssemblyMetadata: boolean;
    /**
     * Determines if the user can view the baseline on the trend.
     */
    canViewBaseline: boolean;
    /**
     * If the user is allowed to edit the baseline for indicators.
     */
    canEditBaseline: boolean;
    canCreateTreeItemLegacy: (
        item: TreeItem,
        isCollectorDependentSystem: boolean
    ) => boolean;
    canEditTreeItemLegacy: (
        item: TreeItem,
        isCollectorDependentSystem: boolean
    ) => boolean;
    canDeleteTreeItemLegacy: (
        item: TreeItem,
        isCollectorDependentSystem: boolean
    ) => boolean;
    canCreateTreeItem: (options: TreeItemActionOptions) => boolean;
    canEditTreeItem: (options: TreeItemActionOptions) => boolean;
    canDeleteTreeItem: (options: TreeItemActionOptions) => boolean;
    canAssignModel: (options: TreeItemActionOptions) => boolean;
    /**
     * User is able to access settings pages.
     */
    canViewSettings: boolean;
    /**
     * If the user is able to view the reports module.
     */
    canViewReports: boolean;
    /**
     * If the user is able to view the reports module without a corporation or location context (global reports).
     */
    canViewGlobalReports: boolean;
    /**
     * User is able to access demo page.
     */
    canViewDemo: boolean;
    /**
     * If the user is able to view the file library.
     */
    canViewFileLibrary: boolean;
    /**
     * If the user is able to upload files to the file library.
     */
    canUploadToFileLibrary: boolean;
    /**
     * If the user is able to edit files in the file library.
     */
    canEditFileLibrary: boolean;
    /**
     * If the user is able to delete files from the file library.
     */
    canDeleteFromFileLibrary: boolean;
    /**
     * Determines if the user can view models.
     */
    canViewModel: boolean;
    canEditModelInEditor: boolean;
    canViewCorporationDashboards: boolean;
    canViewLocationWorkbench: boolean;
    canViewLocationDashboards: boolean;
    canCreateDashboards: boolean;
    canViewDashboardActions: boolean;
    canEditDashboard: (dashboard: IDashboardResponse) => boolean;
    canEditDashboardPermissions: boolean;
    canEditPowerBiReportDashboards: boolean;
    /**
     * Determines whether the user can delete dashboards
     */
    canDeleteDashboard: (dashboard: IDashboardResponse) => boolean;
    canEditMonitoringPoint: boolean;
    canEditNodeSettings: boolean;
    canViewTemplates: boolean;
    canSetFrequentUpdateIntervals: boolean;
    canEditIndicators: boolean;
    canAddIndicator: boolean;
    canViewGlobalSearchIcon: boolean;
    canEditActivityEvent: boolean;
    canViewUsers: boolean;
    canViewTimeRangesSettings: boolean;
    canViewThirdPartySensors: boolean;
    canViewSetupTools: boolean;
    canViewBearingsSettings: boolean;
    canAddBearings: boolean;
    canViewLocationsSettings: boolean;
    canViewIdentityProviders: boolean;
    canEditTree: boolean;
    canEditCustomFilters: boolean;
    canAddSentry: boolean;
    canAddUsers: boolean;
    canAddSaas: boolean;
    canAddSales: boolean;
    canEditUserLocationAssignment: (corporationId: string, locationId: string) => boolean;
    canEditUserCorporationAssignment: (corporationId: string) => boolean;
    canEditUserProperties: boolean;
    canAdminEditUserProperties: boolean;
    canEditUserPersonas: boolean;
    canCreateCorporation: boolean;
    canViewCorporationsSettings: boolean;
    canEditCorporationSettings: boolean;
    canEditCorporationIcon: boolean;
    canEditCorporationMap: boolean;
    canViewCorporationMap: boolean;
    canEditLocationMap: boolean;
    /**
     * Whether or not the user can control other users' notification subscriptions.
     */
    canControlOtherUsersNotificationSubscriptions: boolean;
    canViewHardware: boolean;
    /**
     * Whether or not this user can view Firmware Version on Sensor Settings modal.
     */
    canViewFirmwareVersion: boolean;
    canEditMetadata: boolean;
    canEditAssetDetails: boolean;
    canSetSystemVisibility: boolean;
    canCreateSystem: boolean;
    canEditSystem: boolean;
    canDeleteSystem: boolean;
    canCreateDynamicSystem: boolean;
    canEditFileDetails: boolean;
    canUnlockUser: boolean;
    canEditLocationDetails: boolean;
    canEditLocationSubscriptionService: boolean;
    canViewLocationDetails: boolean;
    canEditLocationValueUnit: boolean;
    canViewAssetTypeSettings: boolean;
    canEditAssetType: boolean;
    canViewAssemblyTypeSettings: boolean;
    canEditAssemblyType: boolean;
    canCreateHardware: boolean;
    canEditBaseStationName: boolean;
    canEditRepeaterName: boolean;
    canViewHardwareAdmin: boolean;
    /**
     * To ensure that we aren't showing hardware issues to end users that were
     * created at a formerly assigned location (KCF production is a good
     * example), we want to filter the related issues on hardware pages to only
     * show issues at the current SD location. Global admins will want to see
     * all related issues, so it will function differently for these users vs
     * the rest of the user base.
     */
    canViewHardwareRelatedIssuesAcrossLocations: boolean;
    /**
     * Whether or not the user can edit the Sensor status
     * on the Nodes Admin Overview Table.
     */
    canEditNodeStatus: boolean;
    canSetNodeInactive: boolean;
    canEditAllowedFrequencies: boolean;
    canViewDraftIssuesReport: boolean;
    canViewRmaSensorReport: boolean;
    canEditScheduledReportSubscribers: boolean;
    canDeleteScheduledReportSubscription: boolean;
    canAddKcfToNotificationSubscriptions: boolean;
    canViewFaultyHardwareReport: boolean;
    canViewNotificationSubscriptionsSettings: boolean;
    canViewGroupNotificationSubscriptions: boolean;
    canDeleteUserNotificationSubscription: boolean;
    /**
     * Whether or not the user can batch create notification subscriptions.
     * Implies the ability to create for users other than self.
     */
    canCreateNotificationSubscriptionsForOthers: boolean;
    canViewIndustries: boolean;
    canViewContainerManagement: boolean;
    canEditContainerManagement: boolean;
    canGrantAccessToAll: boolean;
    useCanEditServiceDefinition: (
        definition: IEdgeDefinitionResponse | undefined
    ) => boolean;
    useCanEditImageRepository: (
        imageRepository: IEdgeImageRepositoryResponse | undefined
    ) => boolean;
    canViewKcfUsers: boolean;
    canViewKcfInfo: boolean;
    canEditKcfInfo: boolean;
    canEditHubName: boolean;
    canEditHubAdmin: boolean;
    canViewIntegrations: boolean;
    canCreateIntegrations: boolean;
    canExportChartImages: boolean;
    canViewWaterfallChart: boolean;
    canCreateCustomMarker: boolean;
    canViewMachineHealthNotificationSubscriptionCategory: (
        category: MachineHealthEventType
    ) => boolean;
    issue: {
        canViewHardwareIssues: boolean;
        canViewInternalIssues: boolean;
        canEditInternalIssue: boolean;
        canBatchArchive: boolean;
        canViewDraftIssues: (mode: IssueMode) => boolean;
        canEditIssue: (issue: IIssueResponseBase) => boolean;
        canEditIssueDescription: (issue: IIssueResponseBase) => boolean;
        canEditInitialRecommendation: (issue: IIssueResponseBase) => boolean;
        canEditComment: (
            comment: IIssueActivityCommentResponse | IIssueActivityCommentReplyResponse
        ) => boolean;
        canDeleteComment: (
            comment: IIssueActivityCommentResponse | IIssueActivityCommentReplyResponse
        ) => boolean;
        canEditActivityChartDataAnnotations: (
            chartData: IIssueActivityChartDataResponse
        ) => boolean;
        canDeleteChartDataFromActivity: (
            chartData: IIssueActivityChartDataResponse
        ) => boolean;
        canEditInitialChartDataAnnotations: (issue: IIssueResponseBase) => boolean;
        canDeleteInitialChartData: (issue: IIssueResponseBase) => boolean;
        canViewHealthRangeTagging: (issue: IIssueResponseBase) => boolean;
        canFilterByHealthRanges: boolean;
        canIndicateFaultOnChartData: boolean;
        canVerifySavings: boolean;
        canSetValueTags: boolean;
        canAutoOpenIssue: boolean;
    };
    canSwitchEnvironment: boolean;
}>;

const Context = PermissionsContextDef;
export const usePermissions = () => {
    const permissionsContext = useContext(Context);
    if (!permissionsContext) {
        throw new Error(
            'Permissions context was not initialized. To fix this error, wrap a parent component in <PermissionsProvider>.'
        );
    }
    return permissionsContext;
};

export function PermissionsProvider({ children }: { children: ReactNode }) {
    const { user } = useUser();
    const { location, corporation } = useRouteEntities();
    const { adminCorporationIds, adminLocationIds } = useOrganizationPermissionsForUser({
        user,
    });
    const permissionsContext = useMemo(
        () =>
            getUserPermissions(user, location, corporation, {
                adminCorporationIds,
                adminLocationIds,
            }),
        [adminCorporationIds, adminLocationIds, corporation, location, user]
    );

    return <Context.Provider value={permissionsContext}>{children}</Context.Provider>;
}
